<script setup lang="ts">
import { secondsToHHMMSS } from '~/utils/video'
import type { TVimeoData } from '~/types/app.type'

type TVimeoEventParams = {
  duration: number
  seconds: number
  percent: number
}

const props = defineProps({
  videoId: {
    type: [Number, String],
    required: true,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
  isFree: {
    type: Boolean,
    default: false,
  },
  categories: {
    type: Array,
    default: () => [],
  },
})

const { $vimeo, $rudderstack } = useNuxtApp()
const vimeoData = ref<TVimeoData>(null)
const player = ref<HTMLIFrameElement | null>(null)

const routeVideo = computed(() => {
  return `https://player.vimeo.com/video/${props.videoId}?loop=false&autoplay=${props.autoplay}&muted=false&playsinline=true&byline=false&portrait=false&title=false&speed=true&transparent=false&&captions=false&pip=false&color=FDBD00`
})

const onVideoEnded = (params: TVimeoEventParams) => {
  if (!params) return

  $rudderstack.trackVideoEnded({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    video_type: props.isFree || props.isPreview ? 'Free' : 'Paid',
  })
}

const onVideoInit = (params: TVimeoEventParams) => {
  if (!params) return

  $rudderstack.trackVideoStarted({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    progress_percentage: `${params.percent * 100}`,
    video_type: props.isFree || props.isPreview ? 'Free' : 'Paid',
  })
}

const onVideoProgress = (params: TVimeoEventParams) => {
  if (!params) return

  $rudderstack.trackVideoProgress({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    progress_percentage: `${params.percent * 100}`,
    video_type: props.isFree || props.isPreview ? 'Free' : 'Paid',
  })
}

onMounted(async () => {
  if (!player.value) return

  const vimeoPlayer = $vimeo.init(player.value)
  vimeoData.value = await $vimeo.getVideoData(props.videoId)

  vimeoPlayer.on('ended', onVideoEnded)
  vimeoPlayer.on('play', onVideoInit)
  vimeoPlayer.on('progress', onVideoProgress)
})
</script>

<template>
  <div class="flex aspect-video w-full">
    <iframe
      ref="player"
      :src="routeVideo"
      allowfullscreen
      class="h-full w-full"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope"
      :title="title"
    />
  </div>
</template>
